import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Modal, Typography, message, Spin, Grid, Layout, Button, Form } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { sendAgentTestingMessage, getVirtualChannels, createVirtualChannel, updateVirtualChannel, deleteVirtualChannel, getVirtualContacts, createVirtualContact, getVirtualContactConversation, deleteVirtualContact } from '../AgentTestingService';
import TestChatInterface from './TestChatInterface/TestChatInterface';
import './AgentTestModal.css';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';
import SidebarList from './SidebarList';
import SidebarNewContact from './SidebarNewContact';
import SidebarNewChannel from './SidebarNewChannel';
import { RTL_LANGUAGES } from '../../../Constants/language';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../Constants/socket';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Header, Sider, Content } = Layout;

const DEFAULT_VIRTUAL_CHANNEL_TYPE = 'facebook_page';
const VIRTUAL_CHANNEL_TYPE_OPTIONS = ['facebook_page', 'instagram', 'whatsapp'];

const AgentTestModal = ({ visible, onClose, agent }) => {
    const { t, i18n } = useTranslation();
    const isRTL = RTL_LANGUAGES.includes(i18n.language?.split('-')[0]);
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const [loading, setLoading] = useState(false);
    const [testConversation, setTestConversation] = useState(null);
    const [virtualChannels, setVirtualChannels] = useState([]);
    const [selectedVirtualChannelId, setSelectedVirtualChannelId] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [sidebarMode, setSidebarMode] = useState('list'); // 'list' | 'new' | 'new-channel' | 'edit-channel' | 'chat'
    const [hoveredContactId, setHoveredContactId] = useState(null);
    const [newContactForm] = Form.useForm();
    const [newChannelForm] = Form.useForm();
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [channelsLoaded, setChannelsLoaded] = useState(false);
    const [creatingContact, setCreatingContact] = useState(false);
    const [creatingChannel, setCreatingChannel] = useState(false);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [conversationByContactId, setConversationByContactId] = useState({});
    const conversationCacheRef = useRef({});
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [deletingContact, setDeletingContact] = useState(false);
    const [editingChannel, setEditingChannel] = useState(null);
    const [channelToDelete, setChannelToDelete] = useState(null);
    const [deleteChannelModalVisible, setDeleteChannelModalVisible] = useState(false);
    const [deletingChannel, setDeletingChannel] = useState(false);

    const selectedVirtualChannel = useMemo(
        () => virtualChannels.find(channel => channel.id === selectedVirtualChannelId) || null,
        [selectedVirtualChannelId, virtualChannels]
    );

    const channelTypeOptions = useMemo(() => {
        return VIRTUAL_CHANNEL_TYPE_OPTIONS.map(type => ({
            value: type,
            label: type === DEFAULT_VIRTUAL_CHANNEL_TYPE
                ? t(type, { defaultValue: 'Facebook page' })
                : `${t(type, { defaultValue: type })} (${t('coming_soon')})`,
            disabled: type !== DEFAULT_VIRTUAL_CHANNEL_TYPE,
        }));
    }, [t]);

    const channelOptions = useMemo(() => virtualChannels.map(channel => ({
        value: channel.id,
        title: channel.name || channel.external_channel_id || channel.id,
        label: (
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {channel.name || channel.external_channel_id || channel.id}
                </span>
                {channel.description && (
                    <span style={{ fontSize: 12, opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {channel.description}
                    </span>
                )}
            </div>
        ),
    })), [virtualChannels]);

    const resetNewChannelForm = useCallback(() => {
        newChannelForm.resetFields();
        newChannelForm.setFieldsValue({
            name: undefined,
            channel_type: channelTypeOptions[0]?.value || DEFAULT_VIRTUAL_CHANNEL_TYPE,
            description: undefined,
        });
    }, [channelTypeOptions, newChannelForm]);

    const fillEditChannelForm = useCallback((channel) => {
        newChannelForm.resetFields();
        newChannelForm.setFieldsValue({
            name: channel?.name,
            description: channel?.description,
        });
    }, [newChannelForm]);

    const upsertConversation = useCallback((contactId, conversation) => {
        if (!contactId) return;
        conversationCacheRef.current[contactId] = conversation;
        setConversationByContactId((prev) => ({ ...prev, [contactId]: conversation }));
    }, []);

    const prefetchConversations = useCallback(async (list) => {
        const ids = (Array.isArray(list) ? list : [])
            .map((c) => c?.id)
            .filter(Boolean);
        if (!ids.length) return;

        await Promise.allSettled(ids.map(async (id) => {
            if (conversationCacheRef.current[id]) return;
            try {
                const conv = await getVirtualContactConversation(id);
                upsertConversation(id, conv);
            } catch (e) {
                // silent prefetch failure
            }
        }));
    }, [upsertConversation]);

    // Get all conversation IDs for socket subscription
    const conversationIds = useMemo(() => {
        const ids = Object.values(conversationByContactId)
            .map(conv => conv?.id)
            .filter(Boolean);
        // Include testConversation if not already in the map
        if (testConversation?.id && !ids.includes(testConversation.id)) {
            ids.push(testConversation.id);
        }
        return ids;
    }, [conversationByContactId, testConversation?.id]);

    // Get all channel IDs for socket subscription (to receive CONVERSATION:UPDATE events)
    const channelIds = useMemo(() => {
        const channels = new Set();
        Object.values(conversationByContactId).forEach(conv => {
            if (conv?.contact?.channel_id) {
                channels.add(conv.contact.channel_id);
            }
        });
        if (testConversation?.contact?.channel_id) {
            channels.add(testConversation.contact.channel_id);
        }
        if (selectedVirtualChannelId) {
            channels.add(selectedVirtualChannelId);
        }
        return Array.from(channels);
    }, [conversationByContactId, selectedVirtualChannelId, testConversation?.contact?.channel_id]);

    // Subscribe to all channels to receive CONVERSATION:UPDATE events
    useSocketSubscription(Resource.CHANNEL, channelIds.length > 0 ? channelIds : null, true);
    // Subscribe to all conversations for granular updates
    useSocketSubscription(Resource.CONVERSATION, conversationIds.length > 0 ? conversationIds : null, true);

    // Handle CONVERSATION:UPDATE events for all conversations
    useSocketEvent(Resource.CONVERSATION, Action.UPDATE, useCallback((data) => {
        if (!data?.id) return;

        // Update in conversationByContactId if exists
        setConversationByContactId(prev => {
            // Find the conversation by id
            const entries = Object.entries(prev);
            let hasChanges = false;
            const updated = {};

            for (const [contactId, conv] of entries) {
                if (conv?.id === data.id) {
                    updated[contactId] = { ...conv, ...data };
                    hasChanges = true;
                } else {
                    updated[contactId] = conv;
                }
            }

            return hasChanges ? updated : prev;
        });

        // Update testConversation if it matches
        if (testConversation?.id === data.id) {
            setTestConversation(prev => ({
                ...prev,
                ...data
            }));
        }
    }, [testConversation?.id, conversationByContactId]));

    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        cardBackground: isDarkMode ? '#1f1f1f' : '#fafafa',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        border: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)'
    };

    const openContactConversation = useCallback(async (contact, shouldOpenChatOnMobile = true) => {
        if (!contact?.id) return;
        setLoading(true);
        try {
            const conversation = await getVirtualContactConversation(contact.id);
            upsertConversation(contact.id, conversation);
            setTestConversation(conversation);
            setSelectedContactId(contact.id);
            if (isMobile && shouldOpenChatOnMobile) setSidebarMode('chat');
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_get_virtual_conversation'));
        } finally {
            setLoading(false);
        }
    }, [isMobile, t, upsertConversation]);

    const loadContactsForChannel = useCallback(async (channelId, options = {}) => {
        if (!agent?.id || !channelId) return;

        const { preferredContactId = null, selectFirst = screens.sm } = options;
        setContactsLoading(true);
        try {
            const list = await getVirtualContacts(agent.id, channelId);
            const normalizedList = Array.isArray(list) ? list : [];
            setContacts(normalizedList);
            prefetchConversations(normalizedList);

            const preferredContact = normalizedList.find(contact => contact.id === preferredContactId);
            const nextContact = preferredContact || (selectFirst ? normalizedList[0] : null);
            if (nextContact) {
                await openContactConversation(nextContact, false);
            }
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_check_virtual_contacts'));
        } finally {
            setContactsLoading(false);
        }
    }, [agent?.id, openContactConversation, prefetchConversations, screens.sm, t]);

    const loadVirtualChannels = useCallback(async (preferredChannelId = null) => {
        if (!agent?.id) return [];

        setChannelsLoading(true);
        setChannelsLoaded(false);
        try {
            const list = await getVirtualChannels(agent.id);
            const normalizedList = Array.isArray(list) ? list : [];
            setVirtualChannels(normalizedList);

            const nextChannel = normalizedList.find(channel => channel.id === preferredChannelId)
                || normalizedList[0]
                || null;

            setSelectedVirtualChannelId(nextChannel?.id || null);
            if (!nextChannel) {
                setContacts([]);
                setSelectedContactId(null);
                setTestConversation(null);
                setSidebarMode('new-channel');
            } else {
                setSidebarMode('list');
            }

            return normalizedList;
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_fetch_virtual_channels', { defaultValue: 'Failed to fetch virtual channels' }));
            return [];
        } finally {
            setChannelsLoading(false);
            setChannelsLoaded(true);
        }
    }, [agent?.id, t]);

    const handleSendMessage = useCallback(async (messageData) => {
        if (!testConversation?.contact?.id) return;

        try {
            const result = await sendAgentTestingMessage(
                testConversation.contact.id,
                messageData
            );
            return result;
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_send_message'));
            throw error;
        }
    }, [testConversation?.contact?.id, t]);

    const handleClose = useCallback(() => {
        setTestConversation(null);
        setVirtualChannels([]);
        setSelectedVirtualChannelId(null);
        setChannelsLoaded(false);
        setSelectedContactId(null);
        setContacts([]);
        setSidebarMode('list');
        setHoveredContactId(null);
        setEditingChannel(null);
        setChannelToDelete(null);
        setDeleteChannelModalVisible(false);
        setConversationByContactId({});
        conversationCacheRef.current = {};
        newContactForm.resetFields();
        newChannelForm.resetFields();
        onClose();
    }, [onClose, newContactForm, newChannelForm]);

    const handleStartNewTest = useCallback(async () => {
        if (!selectedVirtualChannelId) {
            setSidebarMode('new-channel');
            return;
        }
        setSelectedContactId(null);
        setTestConversation(null);
        newContactForm.resetFields();
        setSidebarMode('new');
    }, [newContactForm, selectedVirtualChannelId]);

    // Load virtual channels when modal opens. Conversations are opened only after a channel/contact exists.
    useEffect(() => {
        if (visible && agent?.id) {
            loadVirtualChannels();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, agent?.id]);

    useEffect(() => {
        if (!visible || sidebarMode !== 'new-channel') return;

        resetNewChannelForm();
    }, [resetNewChannelForm, sidebarMode, visible]);

    // Fetch contacts whenever the selected virtual channel changes.
    useEffect(() => {
        if (!visible || !selectedVirtualChannelId) return;

        setContacts([]);
        setSelectedContactId(null);
        setTestConversation(null);
        setConversationByContactId({});
        conversationCacheRef.current = {};
        loadContactsForChannel(selectedVirtualChannelId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVirtualChannelId, visible]);

    const renderLoadingState = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 40px',
            textAlign: 'center'
        }}>
            <Spin size="large" style={{ marginBottom: '24px' }} />
            <Title level={4} style={{ color: themeColors.text, marginBottom: '8px' }}>
                {t('preparing_test_environment')}
            </Title>
            <Text type="secondary">
                {t('setting_up_virtual_contact_and_conversation')}
            </Text>
        </div>
    );

    const renderChatInterface = () => (
        <TestChatInterface
            agent={agent}
            conversation={testConversation}
            onSendMessage={handleSendMessage}
            onStartNewTest={handleStartNewTest}
            onClose={handleClose}
            isMobile={isMobile}
            onConversationUpdated={(conv) => {
                if (!conv) return;
                // Merge instead of replace to preserve nested properties like contact.channel_id
                setTestConversation(prev => prev ? { ...prev, ...conv } : conv);
                const cid = conv?.contact?.id || selectedContactId;
                if (cid) upsertConversation(cid, conv);
            }}
        />
    );

    const renderEmptyChatState = () => (
        <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            background: themeColors.background
        }}>
            <div style={{ maxWidth: 360 }}>
                <Title level={4} style={{ color: themeColors.text }}>
                    {selectedVirtualChannelId
                        ? t('select_or_create_virtual_contact', { defaultValue: 'Select or create a virtual contact' })
                        : t('create_virtual_channel', { defaultValue: 'Create virtual channel' })}
                </Title>
                <Text type="secondary">
                    {selectedVirtualChannelId
                        ? t('select_or_create_virtual_contact_hint', { defaultValue: 'Choose a contact from the sidebar or create one to start testing.' })
                        : t('create_virtual_channel_to_start_testing', { defaultValue: 'Create a virtual channel to start testing this agent.' })}
                </Text>
            </div>
        </div>
    );

    // Handle mobile back button with multi-step navigation
    useMobileFullscreenModalBackHandler(
        visible && isMobile,
        handleClose,
        {
            canGoBack: () => sidebarMode === 'new' || sidebarMode === 'new-channel' || sidebarMode === 'edit-channel' || sidebarMode === 'chat',
            onBack: () => setSidebarMode('list')
        }
    );
    const modalConfig = {
        title: null,
        open: visible,
        onCancel: handleClose,
        footer: null,
        closable: false,
        width: '100vw',
        style: { top: 0, padding: 0, maxWidth: '100vw' },
        styles: {
            body: {
                background: themeColors.background,
                height: '100dvh',
                overflow: 'hidden',
                padding: 0
            },
            content: {
                padding: 0,
                height: '100dvh',
                position: 'fixed',
                inset: 0,
                width: '100vw'
            }
        },
        destroyOnClose: false,
        centered: false,
        mask: true,
        maskClosable: true,
        className: `agent-test-modal ${isDarkMode ? 'agent-test-modal-dark' : 'agent-test-modal-light'}`,
        wrapClassName: `agent-test-modal-wrap ${isDarkMode ? 'agent-test-modal-wrap-dark' : 'agent-test-modal-wrap-light'}`
    };

    const handleChannelChange = useCallback((channelId) => {
        setEditingChannel(null);
        setSelectedVirtualChannelId(channelId);
        setSidebarMode('list');
    }, []);

    const handleAddChannel = useCallback(() => {
        setEditingChannel(null);
        resetNewChannelForm();
        setSidebarMode('new-channel');
    }, [resetNewChannelForm]);

    const handleEditChannel = useCallback((channel) => {
        if (!channel?.id) return;
        setEditingChannel(channel);
        fillEditChannelForm(channel);
        setSidebarMode('edit-channel');
    }, [fillEditChannelForm]);

    const handleDeleteChannel = useCallback((channel) => {
        if (!channel?.id) return;
        setChannelToDelete(channel);
        setDeleteChannelModalVisible(true);
    }, []);

    const handleCreateChannel = useCallback(async () => {
        if (!agent?.id) return;

        try {
            setCreatingChannel(true);
            const values = await newChannelForm.validateFields();
            const created = await createVirtualChannel(
                agent.id,
                values.channel_type,
                values.name,
                values.description
            );

            if (!created?.id) {
                await loadVirtualChannels();
                return;
            }

            setVirtualChannels(prev => {
                const withoutCreated = prev.filter(channel => channel.id !== created.id);
                return [created, ...withoutCreated];
            });
            setSelectedVirtualChannelId(created.id);
            setContacts([]);
            setSelectedContactId(null);
            setTestConversation(null);
            setConversationByContactId({});
            conversationCacheRef.current = {};
            newChannelForm.resetFields();
            setSidebarMode('list');
            message.success(t('channel_created_successfully'));
        } catch (error) {
            if (error?.errorFields) return;
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_create_virtual_channel', { defaultValue: 'Failed to create virtual channel' }));
        } finally {
            setCreatingChannel(false);
        }
    }, [agent?.id, loadVirtualChannels, newChannelForm, t]);

    const handleUpdateChannel = useCallback(async () => {
        if (!editingChannel?.id) return;

        try {
            setCreatingChannel(true);
            const values = await newChannelForm.validateFields();
            const updated = await updateVirtualChannel(editingChannel.id, values);

            const mergedChannel = {
                ...editingChannel,
                ...(updated || {}),
                name: updated?.name ?? values.name,
                description: updated?.description ?? values.description,
            };

            setVirtualChannels(prev => prev.map(channel => (
                channel.id === editingChannel.id ? mergedChannel : channel
            )));
            setEditingChannel(null);
            newChannelForm.resetFields();
            setSidebarMode('list');
            message.success(t('channel_updated_successfully', { defaultValue: 'Channel updated successfully' }));
        } catch (error) {
            if (error?.errorFields) return;
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_update_virtual_channel', { defaultValue: 'Failed to update virtual channel' }));
        } finally {
            setCreatingChannel(false);
        }
    }, [editingChannel, newChannelForm, t]);

    const confirmDeleteChannel = useCallback(async () => {
        if (!channelToDelete?.id) return;

        setDeletingChannel(true);
        try {
            const payload = await deleteVirtualChannel(channelToDelete.id);
            const remainingChannels = virtualChannels.filter(channel => channel.id !== channelToDelete.id);
            const deletingSelectedChannel = selectedVirtualChannelId === channelToDelete.id;

            setVirtualChannels(remainingChannels);
            if (deletingSelectedChannel) {
                const nextChannel = remainingChannels[0] || null;
                setSelectedVirtualChannelId(nextChannel?.id || null);
                setContacts([]);
                setSelectedContactId(null);
                setTestConversation(null);
                setConversationByContactId({});
                conversationCacheRef.current = {};
                if (!nextChannel) {
                    setSidebarMode('new-channel');
                }
            }

            const text = resolveBackendMessage(t, payload);
            message.success(text || t('channel_deleted_successfully', { defaultValue: 'Channel deleted successfully' }));
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_delete_virtual_channel', { defaultValue: 'Failed to delete virtual channel' }));
        } finally {
            setDeletingChannel(false);
            setDeleteChannelModalVisible(false);
            setChannelToDelete(null);
        }
    }, [channelToDelete, selectedVirtualChannelId, t, virtualChannels]);

    const handleSelectContact = useCallback(async (contact) => {
        await openContactConversation(contact);
    }, [openContactConversation]);

    const handleDeleteContact = useCallback((contact) => {
        if (!contact?.id) return;
        setContactToDelete(contact);
        setDeleteModalVisible(true);
    }, []);

    const confirmDeleteContact = useCallback(async () => {
        if (!contactToDelete?.id) return;
        setDeletingContact(true);
        try {
            const payload = await deleteVirtualContact(contactToDelete.id);
            setContacts(prev => prev.filter(c => c.id !== contactToDelete.id));
            setConversationByContactId(prev => {
                const next = { ...prev };
                delete next[contactToDelete.id];
                return next;
            });
            delete conversationCacheRef.current[contactToDelete.id];
            if (selectedContactId === contactToDelete.id) {
                setSelectedContactId(null);
                setTestConversation(null);
            }
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_delete_contact'));
        } finally {
            setDeletingContact(false);
            setDeleteModalVisible(false);
            setContactToDelete(null);
        }
    }, [contactToDelete, selectedContactId, t]);

    const handleCreateContact = useCallback(async () => {
        if (!agent?.id) return;
        if (!selectedVirtualChannelId) {
            setSidebarMode('new-channel');
            return;
        }

        try {
            setCreatingContact(true);
            const values = await newContactForm.validateFields();
            const created = await createVirtualContact(selectedVirtualChannelId, values);
            const list = await getVirtualContacts(agent.id, selectedVirtualChannelId);
            setContacts(Array.isArray(list) ? list : []);
            if (created?.id) {
                const conv = await getVirtualContactConversation(created.id);
                setTestConversation(conv);
                setSelectedContactId(created.id);
                upsertConversation(created.id, conv);
            }
            newContactForm.resetFields();
            setSidebarMode('list');
            message.success(t('test_conversation_started'));
        } catch (error) {
            if (error?.errorFields) return; // validation errors
            message.error(resolveBackendMessage(t, error?.response?.data) || t('failed_to_create_virtual_contact'));
        } finally {
            setCreatingContact(false);
        }
    }, [agent?.id, newContactForm, selectedVirtualChannelId, t, upsertConversation]);

    const renderSidebarList = () => (
        <SidebarList
            virtualChannels={virtualChannels}
            selectedChannelId={selectedVirtualChannelId}
            selectedChannel={selectedVirtualChannel}
            channelsLoading={channelsLoading}
            channelOptions={channelOptions}
            contacts={contacts}
            contactsLoading={contactsLoading}
            selectedContactId={selectedContactId}
            testConversation={testConversation}
            conversationByContactId={conversationByContactId}
            hoveredContactId={hoveredContactId}
            setHoveredContactId={setHoveredContactId}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            t={t}
            onChannelChange={handleChannelChange}
            onAddChannel={handleAddChannel}
            onEditChannel={handleEditChannel}
            onDeleteChannel={handleDeleteChannel}
            onAddContact={() => setSidebarMode('new')}
            onSelectContact={handleSelectContact}
            onDeleteContact={handleDeleteContact}
        />
    );

    const renderSidebarNewContact = () => (
        <SidebarNewContact
            form={newContactForm}
            creatingContact={creatingContact}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            themeColors={themeColors}
            t={t}
            onBack={() => setSidebarMode('list')}
            onCreate={handleCreateContact}
        />
    );

    const renderSidebarNewChannel = () => (
        <SidebarNewChannel
            form={newChannelForm}
            creatingChannel={creatingChannel}
            channelTypeOptions={channelTypeOptions}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            themeColors={themeColors}
            t={t}
            isEditing={sidebarMode === 'edit-channel'}
            canCancel={virtualChannels.length > 0}
            onBack={() => {
                setEditingChannel(null);
                setSidebarMode('list');
            }}
            onCreate={sidebarMode === 'edit-channel' ? handleUpdateChannel : handleCreateChannel}
        />
    );

    const renderSidebarContent = () => {
        if (sidebarMode === 'new-channel' || sidebarMode === 'edit-channel' || (channelsLoaded && virtualChannels.length === 0)) {
            return renderSidebarNewChannel();
        }
        if (sidebarMode === 'new') {
            return renderSidebarNewContact();
        }
        return renderSidebarList();
    };

    const renderMainContent = () => {
        if (loading) return renderLoadingState();
        if (!testConversation?.id) return renderEmptyChatState();
        return renderChatInterface();
    };

    return (
        <Modal {...modalConfig}>
            <Layout style={{ height: '100%', background: themeColors.background }}>
                <Header style={{
                    background: themeColors.background,
                    borderBottom: `1px solid ${themeColors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isMobile && sidebarMode === 'chat' && (
                            <Button type="text" icon={isRTL ? <ArrowRightOutlined /> : <ArrowLeftOutlined />} onClick={() => setSidebarMode('list')} />
                        )}
                        <Title level={4} style={{ margin: 0, color: themeColors.text, fontSize: isMobile ? 16 : undefined }}>{agent?.name || t('agent_testing') || 'Agent testing'}</Title>
                    </div>
                    {(!isMobile || (isMobile && sidebarMode !== 'chat')) && (
                        <Button icon={<CloseOutlined />} type="text" onClick={handleClose} />
                    )}
                </Header>
                <Layout style={{ flex: '1 1 auto', minHeight: 0 }}>
                    {!isMobile && (
                        <Sider width={280} style={{
                            background: isDarkMode ? '#1f1f1f' : '#fff',
                            borderRight: `1px solid ${themeColors.border}`,
                            padding: 16,
                            overflow: 'hidden'
                        }}>
                            {renderSidebarContent()}
                        </Sider>
                    )}
                    <Content style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
                        {isMobile ? (
                            sidebarMode === 'chat' ? (
                                renderMainContent()
                            ) : sidebarMode === 'new-channel' || sidebarMode === 'edit-channel' || (channelsLoaded && virtualChannels.length === 0) ? (
                                renderSidebarNewChannel()
                            ) : sidebarMode === 'new' ? (
                                renderSidebarNewContact()
                            ) : (
                                renderSidebarList()
                            )
                        ) : (
                            renderMainContent()
                        )}
                    </Content>
                </Layout>
            </Layout>

            {/* Delete Contact Modal */}
            <ResponsiveModal
                title={t('delete_contact')}
                open={deleteModalVisible}
                onOk={confirmDeleteContact}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setContactToDelete(null);
                }}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                confirmLoading={deletingContact}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_delete_this_contact')}</div>
            </ResponsiveModal>

            {/* Delete Channel Modal */}
            <ResponsiveModal
                title={t('delete_virtual_channel', { defaultValue: 'Delete virtual channel' })}
                open={deleteChannelModalVisible}
                onOk={confirmDeleteChannel}
                onCancel={() => {
                    setDeleteChannelModalVisible(false);
                    setChannelToDelete(null);
                }}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                confirmLoading={deletingChannel}
                desktopWidth={420}
            >
                <div>
                    {t('are_you_sure_you_want_to_delete_this_channel', { defaultValue: 'Are you sure you want to delete this channel?' })}
                </div>
            </ResponsiveModal>
        </Modal>
    );
};

export default AgentTestModal; 
